import { UIMessage } from 'ai';
import { chatLocalforage } from '@/lib/localforageClient';
// Replace lodash debounce with custom implementation for better tree-shaking
// import { debounce } from 'lodash';
import { nanoid } from 'nanoid';

// Import debounce from our utilities instead of defining it locally
import { debounce } from '@/utils/helpers';

/**
 * Helper function to extract text content from UIMessage parts array
 * AI SDK v5 uses parts structure instead of direct content field
 */
export function getMessageContent(message: UIMessage): string {
  // For backward compatibility, check content first
  if (message.content) return message.content;

  // Extract text from parts array
  if (!message.parts || message.parts.length === 0) return '';

  return message.parts
    .filter((part: any) => part.type === 'text')
    .map((part: any) => part.text)
    .join(' ');
}

// export type ChatMessage = {
//   id: string;
//   role: 'user' | 'assistant';
//   content: string;
//   createdAt: string;
// };

// RFC-069: Document context for multi-doc tracking
export type DocumentContext = {
  docId: string;
  topicId?: string;
  topicTitle?: string;
  selection: { from: number; to: number };
  selectedText: string;
  timestamp: Date;
  editorContent?: string; // Optional full content
};

// UPDATED: EditorContext now contains multiple documents (RFC-069)
export type EditorContext = {
  // New multi-doc structure
  documents?: DocumentContext[]; // Array of all referenced docs
  currentDocId?: string; // Which doc is currently active

  // Legacy single-doc fields (for backward compatibility)
  topicId?: string;
  topicTitle?: string;
  docId?: string;
  selection?: { from: number; to: number };
  selectedText?: string;
  timestamp?: Date;
  initialPrompt?: string; // The initial prompt to send when thread is opened
};

export type ChatThreadMetaData = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  messageCount?: number;
  userMessageCount?: number;
  topicId?: string;
  docId?: string;
  editorContext?: EditorContext;
  archivedAt?: string | null;
  workspaceId?: string;
  scopeType?: string;
  scopeId?: string;
  scopeLabel?: string;
  scopePath?: string;
  lastAskAiAt?: string;
}

export type ChatThreadScope = Pick<
  ChatThreadMetaData,
  'workspaceId' | 'scopeType' | 'scopeId' | 'scopeLabel' | 'scopePath' | 'lastAskAiAt'
>

export type ChatThread = ChatThreadMetaData & {
  messages: UIMessage[];
};

export function normalizeThreadMessages(messages: UIMessage[]): UIMessage[] {
  let changed = false;
  const indexById = new Map<string, number>();
  const normalized: UIMessage[] = [];

  for (const message of messages) {
    const hasStableId =
      typeof message.id === 'string' && message.id.trim().length > 0;
    const stableMessage = hasStableId
      ? message
      : { ...message, id: nanoid() };
    const existingIndex = indexById.get(stableMessage.id);

    if (!hasStableId) {
      changed = true;
    }

    if (existingIndex === undefined) {
      indexById.set(stableMessage.id, normalized.length);
      normalized.push(stableMessage);
      continue;
    }

    changed = true;
    normalized[existingIndex] = stableMessage;
  }

  return changed ? normalized : messages;
}

function normalizeThread(thread: ChatThread): ChatThread {
  const normalizedMessages = normalizeThreadMessages(
    Array.isArray(thread.messages) ? thread.messages : []
  );

  if (normalizedMessages === thread.messages) {
    return thread;
  }

  return {
    ...thread,
    messages: normalizedMessages,
  };
}

const THREADS_KEY = 'chatThreads';
const THREAD_PREFIX = 'chatThread-';

// Helper to extract metadata consistently
function extractThreadMetadata(thread: ChatThread): ChatThreadMetaData {
  const messages = Array.isArray(thread.messages) ? thread.messages : [];
  return {
    id: thread.id,
    name: thread.name,
    createdAt: thread.createdAt,
    updatedAt: thread.updatedAt,
    messageCount: messages.length,
    userMessageCount: messages.filter((message) => message.role === 'user').length,
    topicId: thread.topicId,
    docId: thread.docId,
    editorContext: thread.editorContext,
    archivedAt: thread.archivedAt ?? null,
    workspaceId: thread.workspaceId,
    scopeType: thread.scopeType,
    scopeId: thread.scopeId,
    scopeLabel: thread.scopeLabel,
    scopePath: thread.scopePath,
    lastAskAiAt: thread.lastAskAiAt,
  };
}

export class ChatThreadManager {
  static threadsCache = new Map<string, ChatThread>()
  private static pendingSaves = new Set<Promise<void>>();
  private static threadListMutex = Promise.resolve();
  private static isUnloadHandlerRegistered = false;

  // Initialize beforeunload handler (Bug Fix: Data loss prevention)
  static initialize() {
    if (this.isUnloadHandlerRegistered || typeof window === 'undefined') return;

    window.addEventListener('beforeunload', async (_e) => {
      // Flush all pending saves before browser close
      if (this.pendingSaves.size > 0) {
        // Force immediate save of all pending threads
        const pendingThreads = Array.from(this.threadsCache.values());
        await Promise.all(
          pendingThreads.map(thread =>
            chatLocalforage.setItem(`${THREAD_PREFIX}${thread.id}`, thread)
          )
        );
      }
    });

    this.isUnloadHandlerRegistered = true;
  }

  static async getThreads(): Promise<ChatThreadMetaData[]> {
    this.initialize();
    // Wait for any pending saves to complete
    await Promise.all(this.pendingSaves);
    const threads = (await chatLocalforage.getItem<ChatThreadMetaData[]>(THREADS_KEY)) || [];
    let didHydrate = false;
    const hydratedThreads = await Promise.all(
      threads.map(async (thread) => {
        if (
          typeof thread.messageCount === 'number' &&
          typeof thread.userMessageCount === 'number'
        ) {
          return thread;
        }

        const fullThread = await this.getThread(thread.id);
        if (!fullThread) return thread;
        didHydrate = true;
        return extractThreadMetadata(fullThread);
      })
    );

    if (didHydrate) {
      await this.saveThreads(hydratedThreads);
    }

    return hydratedThreads;
  }

  static async saveThreads(threads: ChatThreadMetaData[]): Promise<void> {
    await chatLocalforage.setItem(THREADS_KEY, threads);
  }

  static async getThread(threadId: string): Promise<ChatThread | null> {
    let thread = this.threadsCache.get(threadId) ?? null;
    if (thread) {
      // console.log('>>> ChatThreadManager.getThread from cache:', threadId, 'messages:', thread.messages.length)
      const normalizedThread = normalizeThread(thread);
      if (normalizedThread !== thread) {
        this.threadsCache.set(threadId, normalizedThread);
        void chatLocalforage.setItem(
          `${THREAD_PREFIX}${threadId}`,
          normalizedThread
        );
      }
      return normalizedThread
    }
    thread = await chatLocalforage.getItem<ChatThread>(`${THREAD_PREFIX}${threadId}`);
    if (thread) {
      const normalizedThread = normalizeThread(thread);
      if (normalizedThread !== thread) {
        void chatLocalforage.setItem(
          `${THREAD_PREFIX}${threadId}`,
          normalizedThread
        );
      }
      // console.log('>>> ChatThreadManager.getThread from storage:', threadId, 'messages:', normalizedThread.messages.length)
      this.threadsCache.set(threadId, normalizedThread)
      return normalizedThread
    } else {
      console.log('>>> ChatThreadManager.getThread: thread not found in storage:', threadId)
    }
    return null;
  }

  private static debouncedSave = debounce(async (thread: ChatThread) => {
    try {
      thread = normalizeThread(thread);
      await chatLocalforage.setItem(`${THREAD_PREFIX}${thread.id}`, thread);
      this.threadsCache.set(thread.id, thread);

      // Only update existing thread metadata, don't add new ones
      // New threads should only be added via createThread/createThreadAtom
      const threads = await this.getThreads();
      const idx = threads.findIndex(t => t.id === thread.id);
      if (idx !== -1) {
        // Update existing thread metadata using helper (Bug Fix: Future-proof)
        threads[idx] = extractThreadMetadata(thread);
        await this.saveThreads(threads);
      }
      // Don't add new threads here - that's handled by createThreadAtom
    } catch (error) {
      console.error('Error saving thread', error);
    }
  }, 2000);

  static async saveThread(thread: ChatThread): Promise<void> {
    const savePromise = new Promise<void>((resolve) => {
      this.debouncedSave(thread);
      setTimeout(resolve, 2000); // Resolve after debounce time
    });
    this.pendingSaves.add(savePromise);
    savePromise.finally(() => this.pendingSaves.delete(savePromise));
  }

  /**
   * Persist a thread immediately, bypassing the 2s debounce. Use when
   * the caller needs a guaranteed durable write — e.g. right after
   * auto-creating a thread from a user's first message, or before
   * navigating away from a live conversation. Also updates the
   * in-memory cache so subsequent getThread() calls see the write.
   */
  static async flushThread(thread: ChatThread): Promise<void> {
    thread = normalizeThread(thread);
    this.threadsCache.set(thread.id, thread);
    await chatLocalforage.setItem(`${THREAD_PREFIX}${thread.id}`, thread);
    const threads = await this.getThreads();
    const idx = threads.findIndex((t) => t.id === thread.id);
    if (idx !== -1) {
      threads[idx] = extractThreadMetadata(thread);
      await this.saveThreads(threads);
    }
  }

  static async createThread(
    name = 'New chat',
    topicId?: string,
    docId?: string,
    id?: string,
    editorContext?: EditorContext,
    scope?: ChatThreadScope,
    messages: UIMessage[] = []
  ): Promise<ChatThread> {
    const now = new Date().toISOString()
    const thread: ChatThread = {
      id: id || nanoid(),
      name,
      createdAt: now,
      updatedAt: now,
      topicId,
      docId,
      editorContext,
      workspaceId: scope?.workspaceId,
      scopeType: scope?.scopeType,
      scopeId: scope?.scopeId,
      scopeLabel: scope?.scopeLabel,
      scopePath: scope?.scopePath,
      lastAskAiAt: scope?.lastAskAiAt,
      messages: normalizeThreadMessages(messages),
    };

    console.log('>>> ChatThreadManager.createThread:', {
      id: thread.id,
      name,
      topicId,
      docId,
      hasEditorContext: !!editorContext,
      scope,
      initialMessages: thread.messages.length
    });

    // Save to cache and storage
    this.threadsCache.set(thread.id, thread)
      await chatLocalforage.setItem(`${THREAD_PREFIX}${thread.id}`, thread);

    // Bug Fix: Atomic thread list update with mutex to prevent race conditions
    this.threadListMutex = this.threadListMutex.then(async () => {
      const threads = await chatLocalforage.getItem<ChatThreadMetaData[]>(THREADS_KEY) || [];
      const threadMetadata = extractThreadMetadata(thread);
      threads.push(threadMetadata);
      await chatLocalforage.setItem(THREADS_KEY, threads);
    });

    await this.threadListMutex;

    console.log('>>> ChatThreadManager.createThread completed:', thread.id);
    return thread;
  }

  static async findReusableScopedThread({
    workspaceId,
    scopeType,
    scopeId,
    maxAgeMs = 30 * 60 * 1000,
  }: {
    workspaceId?: string;
    scopeType?: string;
    scopeId?: string;
    maxAgeMs?: number;
  }): Promise<ChatThreadMetaData | null> {
    if (!workspaceId || !scopeType || !scopeId) {
      return null;
    }

    const threads = await this.getThreads();
    const now = Date.now();

    const matches = threads
      .filter((thread) => {
        if (thread.archivedAt) return false;
        if (thread.workspaceId !== workspaceId) return false;
        if (thread.scopeType !== scopeType) return false;
        if (thread.scopeId !== scopeId) return false;

        const updatedAt = Date.parse(thread.updatedAt);
        if (Number.isNaN(updatedAt)) return false;

        return now - updatedAt <= maxAgeMs;
      })
      .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));

    return matches[0] ?? null;
  }

  static async markThreadAskAiActivity(threadId: string): Promise<void> {
    const thread = await this.getThread(threadId);
    if (!thread) return;

    const now = new Date().toISOString();
    thread.updatedAt = now;
    thread.lastAskAiAt = now;
    await this.flushThread(thread);
  }

  static async deleteThread(threadId: string): Promise<void> {
    console.log('>>> deleting thread', `${THREAD_PREFIX}${threadId}`)
    await chatLocalforage.removeItem(`${THREAD_PREFIX}${threadId}`);
    let threads = await this.getThreads();
    threads = threads.filter(t => t.id !== threadId);
    this.threadsCache.delete(threadId)
    await this.saveThreads(threads);
  }

  static async getMessages(threadId: string): Promise<UIMessage[]> {
    // console.log('>>> ChatThreadManager.getMessages called for thread:', threadId);
    const thread = await this.getThread(threadId);
    const messages = thread ? thread.messages : [];
    // console.log('>>> ChatThreadManager.getMessages returning:', {
    //   threadId,
    //   threadExists: !!thread,
    //   messagesCount: messages.length,
    //   messages: messages.map(m => ({ id: m.id, role: m.role, content: m.content.substring(0, 50) + '...' }))
    // });
    return messages;
  }

  static async addMessage(threadId: string, message: UIMessage): Promise<void> {
    const thread = await this.getThread(threadId);
    if (!thread) return;
    thread.messages = normalizeThreadMessages([...thread.messages, message]);
    thread.updatedAt = new Date().toISOString();
    await this.saveThread(thread);
  }

  static async clearMessages(threadId: string): Promise<void> {
    const thread = await this.getThread(threadId);
    if (!thread) return;
    thread.messages = [];
    thread.updatedAt = new Date().toISOString();
    await this.saveThread(thread);
  }

  static async setMessages(threadId: string, messages: UIMessage[]): Promise<void> {
    console.log('>>> ChatThreadManager.setMessages called:', {
      threadId,
      messagesCount: messages.length,
      messages
    });
    
    const thread = await this.getThread(threadId);
    if (!thread) {
      console.log('>>> ChatThreadManager.setMessages: thread not found:', threadId);
      return;
    }
    
    thread.messages = normalizeThreadMessages(messages);
    thread.updatedAt = new Date().toISOString();
    await this.saveThread(thread);
    
    console.log('>>> ChatThreadManager.setMessages completed for thread:', threadId);
  }

  static async updateThreadName(threadId: string, name: string): Promise<void> {
    console.log('>>> ChatThreadManager.updateThreadName called:', { threadId, name });
    const thread = await this.getThread(threadId);
    if (!thread) {
      console.log('>>> ChatThreadManager.updateThreadName: thread not found:', threadId);
      return;
    }
    thread.name = name;
    thread.updatedAt = new Date().toISOString();
    await this.saveThread(thread);
    console.log('>>> ChatThreadManager.updateThreadName completed:', threadId, name);
  }

  static async setThreadArchived(threadId: string, archived: boolean): Promise<void> {
    const thread = await this.getThread(threadId);
    if (!thread) return;
    thread.archivedAt = archived ? new Date().toISOString() : null;
    thread.updatedAt = new Date().toISOString();
    await this.flushThread(thread);
  }

  static async createThreadFromEditorContext({
    prompt,
    topicId,
    topicTitle,
    docId,
    selection,
    selectedText,
  }: {
    prompt: string;
    topicId?: string;
    topicTitle?: string;
    docId?: string;
    selection: { from: number; to: number };
    selectedText: string;
  }): Promise<ChatThread> {
    const threadId = nanoid();
    const now = new Date().toISOString();

    const thread: ChatThread = {
      id: threadId,
      name: prompt.slice(0, 50) + (prompt.length > 50 ? '...' : ''),
      createdAt: now,
      updatedAt: now,
      topicId,
      docId,
      editorContext: {
        topicId,
        topicTitle,
        docId,
        selection,
        selectedText,
        timestamp: new Date(),
        initialPrompt: prompt, // Store prompt for auto-send on first load
      },
      messages: [], // Don't add initial message - let Chat component send it to trigger AI response
    };

    console.log('>>> ChatThreadManager.createThreadFromEditorContext:', {
      id: thread.id,
      topicId,
      selectionLength: selectedText.length,
      initialPrompt: prompt
    });

    // Save to cache and storage
    this.threadsCache.set(thread.id, thread);
    await chatLocalforage.setItem(`${THREAD_PREFIX}${thread.id}`, thread);

    // Bug Fix: Atomic thread list update with mutex
    this.threadListMutex = this.threadListMutex.then(async () => {
      const threads = await chatLocalforage.getItem<ChatThreadMetaData[]>(THREADS_KEY) || [];
      const threadMetadata = extractThreadMetadata(thread);
      threads.unshift(threadMetadata); // Add to beginning for recent threads
      await chatLocalforage.setItem(THREADS_KEY, threads);
    });

    await this.threadListMutex;

    return thread;
  }

  /**
   * RFC-069: Add a new document context to an existing thread
   */
  static async addDocumentContext(
    threadId: string,
    context: DocumentContext
  ): Promise<void> {
    const thread = await this.getThread(threadId);
    if (!thread) {
      throw new Error(`Thread ${threadId} not found`);
    }

    // RFC-069: Migrate old format to new format if needed
    if (!thread.editorContext) {
      thread.editorContext = {
        documents: [],
        currentDocId: context.docId
      };
    } else if (!thread.editorContext.documents) {
      // Migrate from old single-doc format
      const oldContext = thread.editorContext;
      if (oldContext.docId && oldContext.selectedText && oldContext.selection) {
        thread.editorContext = {
          documents: [{
            docId: oldContext.docId,
            topicId: oldContext.topicId,
            topicTitle: oldContext.topicTitle,
            selection: oldContext.selection,
            selectedText: oldContext.selectedText,
            timestamp: oldContext.timestamp || new Date(),
          }],
          currentDocId: oldContext.docId,
          initialPrompt: oldContext.initialPrompt
        };
      } else {
        thread.editorContext.documents = [];
      }
    }

    // Check if document already exists (update instead of duplicate)
    const existingIndex = thread.editorContext.documents.findIndex(
      doc => doc.docId === context.docId
    );

    if (existingIndex >= 0) {
      // Update existing document context (new selection)
      thread.editorContext.documents[existingIndex] = context;
      console.log(`[ChatThreadManager] RFC-069: Updated context for doc ${context.docId}`);
    } else {
      // Add new document context
      thread.editorContext.documents.push(context);
      console.log(`[ChatThreadManager] RFC-069: Added new doc context: ${context.topicTitle || context.docId}`);
    }

    // Update current doc
    thread.editorContext.currentDocId = context.docId;

    // Save thread
    await this.saveThread(thread);

    console.log(`[ChatThreadManager] RFC-069: Multi-doc context: ${thread.editorContext.documents.length} documents`);
  }

  /**
   * RFC-069: Clear all document contexts (reset)
   */
  static async clearDocumentContexts(threadId: string): Promise<void> {
    const thread = await this.getThread(threadId);
    if (!thread) return;

    if (thread.editorContext) {
      thread.editorContext.documents = [];
      thread.editorContext.currentDocId = undefined;
      await this.saveThread(thread);
      console.log(`[ChatThreadManager] RFC-069: Cleared all document contexts`);
    }
  }

  /**
   * RFC-069: Get all document contexts for a thread
   */
  static async getDocumentContexts(threadId: string): Promise<DocumentContext[]> {
    const thread = await this.getThread(threadId);

    // RFC-069: Migrate old format if needed
    if (thread?.editorContext && !thread.editorContext.documents) {
      const oldContext = thread.editorContext;
      if (oldContext.docId && oldContext.selectedText && oldContext.selection) {
        return [{
          docId: oldContext.docId,
          topicId: oldContext.topicId,
          topicTitle: oldContext.topicTitle,
          selection: oldContext.selection,
          selectedText: oldContext.selectedText,
          timestamp: oldContext.timestamp || new Date(),
        }];
      }
    }

    return thread?.editorContext?.documents || [];
  }
} 
